/** "Migrate from OpenSolar" (pages/Migration.tsx): which step a run is on, its progress, the choices it starts
 * with and the words the page uses (message keys of the integrations namespace). Pure, tested in migration.test.ts. */
import type { MessageKey } from "../i18n/keys";

export type RunStatus = "connected" | "previewing" | "previewed" | "running" | "waiting" | "completed" | "failed" | "cancelled";
export type Step = "connect" | "preview" | "choose" | "run" | "report";

export const STEPS: { key: Step; label: MessageKey }[] = [
  { key: "connect", label: "integrations.migration.step.connect" },
  { key: "preview", label: "integrations.migration.step.preview" },
  { key: "choose", label: "integrations.migration.step.choose" },
  { key: "run", label: "integrations.migration.step.run" },
  { key: "report", label: "integrations.migration.step.report" },
];

/** The one-line command that makes an OpenSolar token (the installer runs it; their password never reaches us). */
export const TOKEN_CURL =
  "curl -s -X POST https://api.opensolar.com/api-token-auth/ -H 'Content-Type: application/json' -d '{\"username\": \"you@example.com\", \"password\": \"YOUR-PASSWORD\"}'";

interface RunLike {
  status: RunStatus;
  progress: { done: number; total: number; files_done: number; files_total: number; waiting_reason?: string | null };
}

/** The step a run is on (no run: connect). A previewed run waits for its choices. */
export function stepOf(run: { status: RunStatus } | null | undefined): Step {
  if (!run) return "connect";
  switch (run.status) {
    case "connected":
    case "previewing":
      return "preview";
    case "previewed":
      return "choose";
    case "running":
    case "waiting":
      return "run";
    case "cancelled":
      return "connect";
    default:
      return "report";
  }
}

/** Whether the page polls the run (the worker is on it). */
export function isWorking(status: RunStatus | null | undefined): boolean {
  return status === "previewing" || status === "running" || status === "waiting";
}

/** 0..1 of the whole import: projects first (three quarters), then files. */
export function progressShare(run: RunLike): number {
  const p = run.progress;
  const projects = p.total ? Math.min(p.done / p.total, 1) : run.status === "completed" ? 1 : 0;
  if (!p.files_total) return projects;
  return Math.min(0.75 * projects + 0.25 * Math.min(p.files_done / p.files_total, 1), 1);
}

/** A size in the reader's locale ("24 kB", "5.0 MB", "3,0 GB"). */
export function formatBytes(bytes: number | null | undefined, locale = "en"): string {
  const unit = (value: number, name: "kilobyte" | "megabyte" | "gigabyte", digits: number) =>
    new Intl.NumberFormat(locale, { style: "unit", unit: name, unitDisplay: "short", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
  if (!bytes) return unit(0, "megabyte", 0);
  if (bytes < 1024 * 1024) return unit(Math.max(1, Math.round(bytes / 1024)), "kilobyte", 0);
  if (bytes < 1024 ** 3) return unit(bytes / 1024 ** 2, "megabyte", bytes < 10 * 1024 ** 2 ? 1 : 0);
  return unit(bytes / 1024 ** 3, "gigabyte", 1);
}

/** OpenSolar's daily file downloads. */
export const FILES_PER_DAY = 1000;

/** "Projects: about 12 minutes. Files: over 3 days (OpenSolar allows 1,000 downloads a day)" as a message. */
export function estimateMessage(
  estimate: { project_minutes: number; file_days: number } | null | undefined,
): { key: MessageKey; values: { minutes: number; days: number; limit: number } } | null {
  if (!estimate) return null;
  const values = { minutes: Math.max(1, Math.round(estimate.project_minutes)), days: estimate.file_days, limit: FILES_PER_DAY };
  return { key: estimate.file_days <= 1 ? "integrations.migration.estimateSameDay" : "integrations.migration.estimateDays", values };
}

export interface Choices {
  stages: string[];
  created_from: string | null;
  created_to: string | null;
  only_with_system: boolean;
  skip_duplicates: boolean;
  files: boolean;
}

/** Every stage but lost ones, every date, duplicates skipped, files on. */
export function defaultChoices(stages: { stage: string; status: string }[]): Choices {
  const lost = stages.filter((s) => s.status === "lost").map((s) => s.stage);
  return {
    stages: lost.length ? stages.filter((s) => s.status !== "lost").map((s) => s.stage) : [],
    created_from: null,
    created_to: null,
    only_with_system: false,
    skip_duplicates: true,
    files: true,
  };
}

/** The choices as the API takes them: every stage selected = no stage filter. */
export function choicesBody(choices: Choices, allStages: string[]): Choices {
  const every = allStages.length > 0 && allStages.every((s) => choices.stages.includes(s));
  return { ...choices, stages: every ? [] : choices.stages };
}

/** How many projects the chosen stages hold. */
export function chosenCount(stages: { stage: string; count: number }[], chosen: string[]): number {
  return stages.filter((s) => !chosen.length || chosen.includes(s.stage)).reduce((n, s) => n + s.count, 0);
}

export const ITEM_LABELS: Record<string, { label: MessageKey; tone?: "ok" | "warn" | "danger" | "info" }> = {
  pending: { label: "integrations.migration.item.pending", tone: "info" },
  imported: { label: "integrations.migration.item.imported", tone: "ok" },
  updated: { label: "integrations.migration.item.updated", tone: "ok" },
  unchanged: { label: "integrations.migration.item.unchanged" },
  partial: { label: "integrations.migration.item.partial", tone: "warn" },
  skipped: { label: "integrations.migration.item.skipped" },
  failed: { label: "integrations.migration.item.failed", tone: "danger" },
};

/** The pipeline status an OpenSolar stage maps to, in English (pages not yet in the catalogs; Migration.tsx uses the
 * message `integrations.migration.leadStatus`). */
export const LEAD_STATUS_LABELS: Record<string, string> = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  unqualified: "Unqualified",
  won: "Won",
  lost: "Lost",
};

/** The reward's outcome in one sentence (the message takes the badge's name as `badge`). */
export function rewardKey(status: string | null | undefined): MessageKey | null {
  switch (status) {
    case "approved":
      return "integrations.migration.reward.approved";
    case "queued":
      return "integrations.migration.reward.queued";
    case "already":
      return "integrations.migration.reward.already";
    case "not_eligible":
      return "integrations.migration.reward.notEligible";
    default:
      return null;
  }
}

/** The status of a run, as a chip: its tone; the words are the message `integrations.migration.runStatus`. */
export function runTone(status: RunStatus): "ok" | "warn" | "danger" | "info" | undefined {
  return runChip(status).tone;
}

/** The status of a run, as a chip, in English (pages not yet in the catalogs; Migration.tsx uses runTone). */
export function runChip(status: RunStatus): { label: string; tone?: "ok" | "warn" | "danger" | "info" } {
  switch (status) {
    case "previewing":
      return { label: "Reading your account", tone: "info" };
    case "previewed":
      return { label: "Ready to import", tone: "info" };
    case "running":
      return { label: "Importing", tone: "info" };
    case "waiting":
      return { label: "Continues tomorrow", tone: "warn" };
    case "completed":
      return { label: "Done", tone: "ok" };
    case "failed":
      return { label: "Stopped", tone: "danger" };
    case "cancelled":
      return { label: "Disconnected" };
    default:
      return { label: "Connected", tone: "info" };
  }
}
